import { toast } from "@devhop/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { templateKeys } from "../api/queries";
import { templateApi } from "../api/client";
import { useTemplatesQuery } from "../api/queries";
import { TemplateTour, useTemplateTour } from "../tour/TemplateTour";
import { TemplateFilter } from "../ui/TemplateFilter";
import { TemplateTable } from "../ui/TemplateTable";
import { TemplateToolbar } from "../ui/TemplateToolbar";

export function TemplateListPage() {
  const nav = useNavigate({ from: "/app/template" });
  const search: OffsetPageQueryDTO = useSearch({ from: "/app/template" });
  const qc = useQueryClient();
  const tour = useTemplateTour();

  const list = useTemplatesQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const canManage = useActionPermission(["template:create"]);

  // Activate mutation - set status to "active"
  const activateMutation = useMutation({
    mutationFn: (id: string) => templateApi.update(id, { status: "active" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: templateKeys.all });
      toast.success("ເປີດການໃຊ້ງານຮ່າງເອກະສານສຳເລັດ");
    },
    onError: () => {
      toast.error("ເປີດການໃຊ້ງານຮ່າງເອກະສານລົ້ມເຫຼວ");
    },
  });

  // Deactivate mutation - set status to "archived"
  const deactivateMutation = useMutation({
    mutationFn: (id: string) => templateApi.update(id, { status: "archived" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: templateKeys.all });
      toast.success("ປິດການໃຊ້ງານຮ່າງເອກະສານສຳເລັດ");
    },
    onError: () => {
      toast.error("ປິດການໃຊ້ງານຮ່າງເອກະສານລົ້ມເຫຼວ");
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => templateApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: templateKeys.all });
      toast.success("ລຶບຮ່າງເອກະສານສຳເລັດ");
    },
    onError: () => {
      toast.error("ລຶບຮ່າງເອກະສານລົ້ມເຫຼວ");
    },
  });

  const handleActivate = (template: { id: string }) => {
    activateMutation.mutate(template.id);
  };

  const handleDeactivate = (template: { id: string }) => {
    deactivateMutation.mutate(template.id);
  };

  const handleDelete = (template: { id: string }) => {
    deleteMutation.mutate(template.id);
  };

  return (
    <>
      <Header />

      <Main>
        <TemplateTour run={tour.run} onCallback={tour.handleJoyrideCallback} />

        <TemplateToolbar
          canManage={!!canManage}
          onCreate={() => nav({ to: "/app/template/new" })}
          onStartTour={tour.startTour}
        />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <TemplateFilter />

          <TemplateTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onView={(template) =>
              nav({ to: `/app/template/${template.id}` })
            }
            onEdit={(template) =>
              nav({ to: `/app/template/${template.id}/edit` })
            }
            onDelete={handleDelete}
            onActivate={handleActivate}
            onDeactivate={handleDeactivate}
          />
        </div>
      </Main>
    </>
  );
}
